/**
 * Data Page Component
 *
 * Central hub for managing data sources and imports for Rocket Pricing.
 *
 * Features:
 * - Entity selector (Offerings, Vendors, Costs, Products - only Offerings enabled in MVP)
 * - Three-tab structure: IMPORT | [ENTITY] | HISTORY
 *   - Middle tab label is dynamic based on selected entity (e.g., "OFFERINGS", "COSTS")
 *   - Falls back to "MANAGE" if no entity label found in metadata
 * - Platform sync with auto-activate toggle
 * - Draft review and activation workflow
 * - Active offerings catalog
 *
 * Based on redesigned UX: frontend/react/src/pages/workflow/data/UX_DESIGN.md
 */

import { useState, useMemo } from 'react'
import { Space, Tabs, message } from 'antd'
import { ShoppingOutlined, DollarOutlined, FieldNumberOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { NoOrgSelectedPrompt } from '@/components/NoOrgSelectedPrompt'
import { ModuleNavSidebar } from '@/components/navigation'
import { apiService } from '@/services/apiService'
import { errorService } from '@/services/errorService'
import { ImportTab } from './data/components/ImportTab'
import { CatalogTab } from './data/components/CatalogTab'
import { HistoryTab } from './data/components/HistoryTab'
import { SyncResultsModal } from './data/components/SyncResultsModal'
import type { EntityType, PullResult } from './data/types'
import type { Connection } from '@/types/connection.types'
import type { InboundSyncConfig, OutboundSyncConfig } from '@/components/connections/ConnectionSyncStatusCard'
import type { NavItem, NavSection } from '@/types/navigation.types'

// CSS for sidebar layout
import '@/components/navigation/ModuleNavSidebar.css'

// Using Tabs items prop (TabPane is deprecated)

const LOCALSTORAGE_KEY_AUTO_ACTIVATE = 'rp.data.autoActivate'

type MainTab = 'import' | 'catalog' | 'history'

export default function DataPage() {
  const currentUser = useCurrentUser()
  const queryClient = useQueryClient()

  // State
  const [selectedEntity, setSelectedEntity] = useState<EntityType>('offerings')
  const [activeTab, setActiveTab] = useState<MainTab>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem('dataPage.activeTab')
      if (saved === 'import' || saved === 'catalog' || saved === 'history') {
        return saved
      }
    } catch (error) {
      console.error('Failed to restore tab state:', error)
    }
    return 'import'
  })

  // Sync results modal state
  const [syncResultsVisible, setSyncResultsVisible] = useState(false)
  const [syncResultsData, setSyncResultsData] = useState<{
    connectionName: string
    platform: string
    result: PullResult
  } | null>(null)

  // Auto-activate settings state (per connection)
  const [autoActivateSettings, setAutoActivateSettings] = useState<Record<string, boolean>>(() => {
    // Initialize from localStorage
    const settings: Record<string, boolean> = {}
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith(LOCALSTORAGE_KEY_AUTO_ACTIVATE + '.')) {
          const connectionUid = key.replace(LOCALSTORAGE_KEY_AUTO_ACTIVATE + '.', '')
          settings[connectionUid] = localStorage.getItem(key) === 'true'
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    return settings
  })

  // Persist tab changes to localStorage
  const handleTabChange = (key: string) => {
    const newTab = key as MainTab
    setActiveTab(newTab)
    try {
      localStorage.setItem('dataPage.activeTab', newTab)
    } catch (error) {
      console.error('Failed to save tab state:', error)
    }
  }

  // Guard: No org selected
  if (!currentUser.data?.currentOrganization?.id) {
    return <NoOrgSelectedPrompt />
  }

  // Fetch draft count (lightweight query for status bar)
  const { data: draftCount = 0 } = useQuery({
    queryKey: ['offerings', 'count', 'drafts'],
    queryFn: async () => {
      const filters = JSON.stringify([
        { key: 'lifecycleState', constraint_type: 'EQUAL', value: 'draft' },
        { key: 'deletedAt', constraint_type: 'IS_NULL' },
      ])
      const response = await apiService.get<{ count: number }>('/offerings/count?filters=' + encodeURIComponent(filters))

      if (!response.success) {
        console.error('Failed to fetch draft count:', response.errors)
        return 0
      }

      return response.data?.count ?? 0
    },
    staleTime: 1000 * 60, // 1 minute - updates after syncs
    refetchInterval: 1000 * 60, // Auto-refresh every minute
  })


  // Fetch connections
  const { data: connections = [], isLoading: loadingConnections } = useQuery({
    queryKey: ['connections', 'active'],
    queryFn: async () => {
      const response = await apiService.post<Connection[]>('/connections/search', {
        filters: [
          { key: 'state', constraint_type: 'EQUAL', value: 'active' },
          { key: 'deletedAt', constraint_type: 'IS_NULL' },
        ],
        order_by: [{ key: 'createdAt', direction: 'asc' }],
      })

      if (!response.success) {
        console.error('Failed to fetch connections:', response.errors)
        return []
      }

      return response.data ?? []
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - connections change rarely
  })


  // Build inbound/outbound configs from connections and state
  const inboundConfigs: Record<string, InboundSyncConfig> = {}
  const outboundConfigs: Record<string, OutboundSyncConfig> = {}

  connections.forEach((conn) => {
    inboundConfigs[conn.uid] = {
      targetTable: 'Offerings',
      lastSyncAt: conn.metadata?.lastTestResult?.testedAt
        ? new Date(conn.metadata.lastTestResult.testedAt).toLocaleString()
        : undefined,
      autoPromote: autoActivateSettings[conn.uid] ?? false,
    }

    outboundConfigs[conn.uid] = {
      configured: false, // TODO: Check if outbound sync is configured
    }
  })

  // Sync mutation (Book's fix: use platform parameter)
  const syncMutation = useMutation({
    mutationFn: async ({ connectionUid, platform }: { connectionUid: string; platform: string }) => {
      const autoActivate = autoActivateSettings[connectionUid] ?? false

      const response = await apiService.post<PullResult>('/integrations/pull', {
        platform, // CRITICAL: Use platform parameter (Book's fix)
        connectionId: connectionUid,
        autoActivate,
      })

      if (!response.success) {
        throw new Error(response.errors?.[0]?.message || 'Sync failed')
      }

      return response.data!
    },
    onSuccess: (result, variables) => {
      // Find connection name
      const connection = connections.find((c) => c.uid === variables.connectionUid)
      const name = connection?.name || 'platform'

      // Show detailed results modal
      setSyncResultsData({
        connectionName: name,
        platform: connection?.platform || 'unknown',
        result,
      })
      setSyncResultsVisible(true)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['offerings', 'count', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['offerings', 'drafts'] })
      queryClient.invalidateQueries({ queryKey: ['connections'] })
    },
    onError: (error: Error) => {
      errorService.showError('Sync Failed', error.message, [
        { code: 'SYNC_ERROR', message: error.message },
      ])
    },
  })

  // Handlers
  const handleReviewDrafts = () => {
    setActiveTab('catalog')
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSyncNow = (connectionUid: string) => {
    const connection = connections.find((c) => c.uid === connectionUid)
    if (!connection) {
      message.error('Connection not found')
      return
    }

    syncMutation.mutate({
      connectionUid,
      platform: connection.platform, // Use platform from connection
    })
  }

  // Handle auto-activate toggle - updates React state AND persists to localStorage
  const handleToggleAutoActivate = (connectionUid: string, value: boolean) => {
    // Update React state (triggers immediate re-render)
    setAutoActivateSettings(prev => ({
      ...prev,
      [connectionUid]: value,
    }))

    // Persist to localStorage
    try {
      const key = LOCALSTORAGE_KEY_AUTO_ACTIVATE + '.' + connectionUid
      localStorage.setItem(key, value.toString())
    } catch (error) {
      console.error('Failed to save auto-activate setting:', error)
    }
  }

  // Determine default tab based on draft count
  const defaultTab = draftCount > 0 ? 'catalog' : 'import'

  // Section definitions for sidebar grouping
  const navSections: NavSection[] = useMemo(
    () => [
      { key: 'master-data', label: 'Master Data', order: 1 },
      // Future sections:
      // { key: 'inventory', label: 'Inventory', order: 2 },
      // { key: 'transactions', label: 'Transaction Data', order: 3 },
    ],
    []
  )

  // Navigation items for sidebar (static for now, will be API-driven in future)
  const navItems: NavItem[] = useMemo(
    () => [
      { key: 'offerings', label: 'Offerings', icon: <ShoppingOutlined />, tier: 'GA', section: 'master-data' },
      { key: 'uoms', label: 'Units of Measure', icon: <FieldNumberOutlined />, tier: 'Early Access', section: 'master-data' },
      { key: 'currencies', label: 'Currencies', icon: <DollarOutlined />, tier: 'Dev', section: 'master-data' },
      // More entities will be added as they become available
      // { key: 'vendors', label: 'Vendors', icon: <ShopOutlined />, tier: 'Dev', section: 'master-data' },
      // { key: 'customers', label: 'Customers', icon: <TeamOutlined />, tier: 'Dev', section: 'master-data' },
    ],
    []
  )

  // Dynamic tab label: Use selected entity's label from navItems, fallback to "MANAGE"
  const entityTabLabel = useMemo(() => {
    const selectedNav = navItems.find((item) => item.key === selectedEntity)
    return selectedNav?.label?.toUpperCase() || 'MANAGE'
  }, [navItems, selectedEntity])

  return (
    <div className="page-with-sidebar">
      {/* Module Navigation Sidebar */}
      <ModuleNavSidebar
        items={navItems}
        sections={navSections}
        selectedKey={selectedEntity}
        onSelect={(key) => setSelectedEntity(key as EntityType)}
        storageKey="dataPage.sidebar"
      />

      {/* Main Content */}
      <div className="page-content">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Main Tabs */}
          <Tabs
            defaultActiveKey={defaultTab}
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                key: 'import',
                label: 'IMPORT',
                children: (
                  <ImportTab
                    connections={connections}
                    loadingConnections={loadingConnections}
                    inboundConfigs={inboundConfigs}
                    outboundConfigs={outboundConfigs}
                    onSyncNow={handleSyncNow}
                    onToggleAutoActivate={handleToggleAutoActivate}
                  />
                ),
              },
              {
                key: 'catalog',
                label: entityTabLabel,
                children: <CatalogTab selectedEntity={selectedEntity} />,
              },
              {
                key: 'history',
                label: 'HISTORY',
                children: <HistoryTab />,
              },
            ]}
          />

          {/* Sync Results Modal */}
          {syncResultsData && (
            <SyncResultsModal
              visible={syncResultsVisible}
              connectionName={syncResultsData.connectionName}
              platform={syncResultsData.platform}
              result={syncResultsData.result}
              onClose={() => setSyncResultsVisible(false)}
              onReviewDrafts={() => {
                setSyncResultsVisible(false)
                handleReviewDrafts()
              }}
            />
          )}
        </Space>
      </div>
    </div>
  )
}
